import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Percent, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { platformAPI } from '../utils/api';
import { AdminUsersPanel } from '../components/admin/AdminUsersPanel';
import { SquareStatusPanel } from '../components/admin/SquareStatusPanel';

/**
 * Platform Settings (MASTER / LMASTER only).
 * Currently holds the platform-wide default service-fee percent.
 * Dropdown enforces 1% – 8% in 0.5% increments to eliminate typo risk.
 */
const PlatformSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pct, setPct] = useState(0.04);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const userRoles = useMemo(() => user?.roles || (user?.role ? [user.role] : []), [user]);
  const isMaster = userRoles.some((r) => ['MASTER', 'LMASTER'].includes(r));
  const isMasterOnly = userRoles.includes('MASTER');

  // 0% (waived) + 1.0% → 8.0% in 0.5% steps → 16 options.
  const options = useMemo(() => {
    const arr = [0];
    for (let p = 1.0; p <= 8.0 + 1e-9; p += 0.5) {
      const rounded = Math.round(p * 10) / 10;
      arr.push(rounded);
    }
    return arr;
  }, []);

  useEffect(() => {
    if (!isMasterOnly) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await platformAPI.getSettings();
        setPct(data.service_fee_pct ?? 0.04);
      } catch {
        toast.error('Failed to load platform settings');
      } finally {
        setLoading(false);
      }
    })();
  }, [isMaster]);

  const save = async () => {
    setSaving(true);
    try {
      await platformAPI.updateSettings({ service_fee_pct: pct });
      toast.success(`Service fee default saved: ${(pct * 100).toFixed(1).replace(/\.0$/, '')}%`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isMaster) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <p className="text-[#9E9E9E]" data-testid="platform-settings-denied">Platform admin access required</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#E5E5E5]" data-testid="platform-settings-page">
      <nav className="bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-[#9E9E9E] hover:text-white transition-colors text-sm"
              data-testid="platform-settings-back"
            >
              <ArrowLeft size={18} /> Back to Dashboard
            </button>
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>
              Platform Settings
            </h1>
            <div />
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {isMasterOnly && (
        <div className="bg-[#111] border border-[#1F1F1F] rounded-2xl p-6 sm:p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-11 h-11 rounded-full bg-[#0096FF]/15 flex items-center justify-center flex-shrink-0">
              <Percent size={22} weight="duotone" className="text-[#0096FF]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                Default Service Fee
              </h2>
              <p className="text-sm text-[#9E9E9E] mt-1 max-w-lg">
                The percentage The Show Master adds on top of entry &amp; accommodation totals at
                checkout. Applied to every show that hasn't set its own per-show override.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="shimmer h-10 w-40 rounded-md" />
          ) : (
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-xs uppercase tracking-wider text-[#9E9E9E] font-semibold">
                  Service Fee %
                </label>
                <select
                  value={pct}
                  onChange={(e) => setPct(parseFloat(e.target.value))}
                  className="bg-[#0A0A0A] border border-[#262626] rounded-md px-4 py-2.5 text-white text-base font-bold min-w-[140px] focus:outline-none focus:border-[#0096FF] transition-colors"
                  data-testid="platform-fee-select"
                >
                  {options.map((pctOption) => {
                    const val = pctOption / 100;
                    let label;
                    if (pctOption === 0) label = '0% (Waived)';
                    else if (pctOption % 1 === 0) label = `${pctOption}%`;
                    else label = `${pctOption.toFixed(1)}%`;
                    return (
                      <option key={pctOption} value={val}>{label}</option>
                    );
                  })}
                </select>
              </div>
              <button
                onClick={save}
                disabled={saving}
                className="bg-[#0096FF] hover:bg-[#0096FF]/90 disabled:opacity-60 text-white font-bold text-sm px-5 py-2.5 rounded-md transition-colors flex items-center gap-2"
                data-testid="platform-fee-save"
              >
                <Check size={16} weight="bold" /> {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}

          <p className="mt-6 text-xs text-[#6B6B6B] leading-relaxed">
            Range: 0% (waived) or 1.0% – 8.0% in 0.5% increments. A dropdown is used (instead of free-text) to
            prevent accidental typos like &quot;40%&quot;. Producers cannot change this. Platform admins may
            set a per-show override on the Show Edit page.
          </p>
        </div>
        )}

        <SquareStatusPanel />

        <AdminUsersPanel />
      </div>
    </div>
  );
};

export default PlatformSettings;
